import { useState } from "react";
import { anyService } from "@/services/breeze/AnyService";
import { RlsUser, UpdateRlsUser } from "@/types/breeze/Any";
import { useUsers } from "@/hooks/useCommonQueries";
import SelectField from "@/components/form/SelectField";
import { BasicTable, Column } from "@/components/ui/BasicTable";
import { Trash2, Plus } from "lucide-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Formik, Form } from "formik";
import SideDrawer from "@/components/ui/SideDrawer";
import withTenantAdminRequired from "@/hoc/withTenantAdminRequired";
import {
  Button,
  Box,
  Flex,
  Grid,
  Text,
  IconButton,
  Switch,
} from "@radix-ui/themes";
import CheckboxField from "../form/CheckboxField";

interface EntityRlsUsersProps {
  entityName: string;
  entityId: number;
}

const EntityRlsUsers = ({ entityName, entityId }: EntityRlsUsersProps) => {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const queryClient = useQueryClient();
  const { data: users, isLoading: isLoadingUsers } = useUsers();

  // Query to fetch RLS users
  const { data: rlsUsers = [], isLoading } = useQuery({
    queryKey: ["rls-users", entityName, entityId],
    queryFn: () => anyService.getRlsUsers(entityName, entityId),
  });

  // Mutation to add or update RLS user
  const updateRlsUserMutation = useMutation({
    mutationFn: (userData: UpdateRlsUser) => {
      return anyService.updateRlsUser(entityName, entityId, userData);
    },
    onSuccess: () => {
      // Invalidate and refetch
      queryClient.invalidateQueries({
        queryKey: ["rls-users", entityName, entityId],
      });
      queryClient.invalidateQueries({
        queryKey: ["entity-data", entityName, entityId],
      });
      // Close the drawer on success
      setIsDrawerOpen(false);
    },
  });

  const deleteRlsUserMutation = useMutation({
    mutationFn: (rlsUserId: number) => {
      return anyService.deleteRlsUser(entityName, entityId, rlsUserId);
    },
    onSuccess: () => {
      // Invalidate and refetch
      queryClient.invalidateQueries({
        queryKey: ["rls-users", entityName, entityId],
      });
      queryClient.invalidateQueries({
        queryKey: ["entity-data", entityName, entityId],
      });
    },
  });

  // Get filtered users for the select dropdown
  const filteredUsers =
    users?.filter(
      (user) => !rlsUsers.some((rlsUser) => rlsUser.user_id === user.id),
    ) || [];

  // Columns for the RLS users table
  const columns: Column<RlsUser>[] = [
    {
      header: "User",
      accessor: "user",
      cellRenderer: (row: RlsUser) =>
        `${row.user.first_name} ${row.user.last_name} (${row.user.email})`,
    },
    {
      header: "Read Only",
      accessor: "readonly",
      cellRenderer: (row: RlsUser) => (
        <>
          <Switch
            checked={row.readonly}
            onCheckedChange={(value) =>
              updateRlsUserMutation.mutate({
                user_id: row.user_id,
                readonly: value,
              })
            }
            size="2"
          />
        </>
      ),
    },
    {
      header: "Actions",
      accessor: "id",
      cellRenderer: (row: RlsUser) => (
        <IconButton
          size="1"
          variant="ghost"
          color="red"
          onClick={() => {
            if (
              confirm("Are you sure you want to remove this user's access?")
            ) {
              // Use a special flag for deletion
              deleteRlsUserMutation.mutate(row.id);
            }
          }}
          title="Remove user"
        >
          <Trash2 size={16} />
        </IconButton>
      ),
    },
  ];

  return (
    <Box>
      <Flex justify="end" mb="4">
        <Button
          onClick={() => setIsDrawerOpen(true)}
          disabled={filteredUsers.length === 0}
          size="2"
        >
          <Plus size={16} />
          Add User Access
        </Button>
      </Flex>

      {isLoading ? (
        <Flex justify="center" align="center" p="8">
          <Text color="gray">Loading...</Text>
        </Flex>
      ) : rlsUsers.length === 0 ? (
        <Box py="8">
          <Text align="center" color="gray" as="p">
            No users have been granted access to this record
          </Text>
        </Box>
      ) : (
        <BasicTable columns={columns} data={rlsUsers} isLoading={isLoading} />
      )}

      {/* Side drawer for adding user access */}
      <SideDrawer
        isOpen={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        title="Add User Access"
      >
        <Box p="4">
          {filteredUsers.length === 0 ? (
            <Box py="8">
              <Text align="center" color="gray" as="p">
                All users have already been granted access to this record
              </Text>
            </Box>
          ) : (
            <Formik
              initialValues={{
                user_id: 0,
                readonly: true,
              }}
              onSubmit={(values) => {
                updateRlsUserMutation.mutate({
                  user_id: values.user_id,
                  readonly: values.readonly,
                });
              }}
            >
              {({ values }) => (
                <Form>
                  <Flex direction="column" gap="4">
                    <SelectField
                      name="user_id"
                      label="User"
                      required
                      type="number"
                      disabled={isLoadingUsers}
                    >
                      {filteredUsers.map((user) => (
                        <SelectField.Item key={user.id} value={user.id}>
                          {user.first_name} {user.last_name} ({user.email})
                        </SelectField.Item>
                      ))}
                    </SelectField>

                    <CheckboxField name="readonly" label="Read Only Access" />

                    <Grid columns="2" gap="4">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => setIsDrawerOpen(false)}
                        style={{ width: "100%" }}
                      >
                        Cancel
                      </Button>
                      <Button
                        type="submit"
                        disabled={
                          !values.user_id || updateRlsUserMutation.isPending
                        }
                        style={{ width: "100%" }}
                      >
                        {updateRlsUserMutation.isPending
                          ? "Adding..."
                          : "Add User"}
                      </Button>
                    </Grid>
                  </Flex>
                </Form>
              )}
            </Formik>
          )}
        </Box>
      </SideDrawer>
    </Box>
  );
};

export default withTenantAdminRequired(EntityRlsUsers);
